import { useCallback, useId, useMemo, useState } from 'react';

import { INDEX_X_DATA_KEY } from '../../constants';
import type { ChartContextValue, ReferenceLineMetadata, SeriesDisplayMetadata } from '../../types/ChartContext';
import type { ChartDataPoint } from '../../types/ChartDataPoint';
import type { ReferenceLineData } from '../../types/ReferenceLineData';
import type { XScaleType } from '../../types/Scale';
import type { XDataKey } from '../../types/XDataKey';
import { createColorScale } from '../../utils/colorScale';
import type { YAxisConfig } from '../../utils/extractYAxisConfig';
import type { DataType } from '../../utils/formatters';
import {
  calculateNiceDomainWithTicks,
  computeStackedYMax,
  computeYDomain,
  isEffectivelyInfinite,
} from '../../utils/scaleHelpers';
import { filterDataByZoomDomain } from '../../utils/zoomDomainFilter';
import { ChartContext } from '../ChartContext';
import { useSyncedZoomContext } from '../SyncedChartProvider';
import { TooltipProvider } from '../Tooltip';

export interface ChartProviderProps {
  /**
   * The value object for the chart provider.
   */
  value: ChartContextValue;

  /**
   * Chart components (XAxis, YAxis, Series, etc.)
   */
  children: React.ReactNode;
}

export type ChartProviderStateProps = {
  /**
   * Whether drag-to-zoom is disabled
   * @default false
   */
  disableDragToZoom?: boolean;

  /**
   * Type of chart ('line' or 'column')
   */
  chartType: 'line' | 'column';

  /**
   * The shared data array for all series in the chart.
   * Each series accesses this data using its dataKey.
   */
  data?: ChartDataPoint[] | null;

  /**
   * Optional array of reference line data.
   * ReferenceLine components read from this using their referenceKey prop.
   */
  referenceLineData?: ReferenceLineData[];

  /**
   * Whether the chart is loading - if yes will show a skeleton chart.
   * @default false
   */
  isLoading?: boolean;

  /**
   * The key in data objects that represents the x-axis value.
   * Extracted from XAxis component props by the chart component.
   * @default INDEX_X_DATA_KEY (array indices)
   */
  xDataKey?: XDataKey;

  /**
   * The scale type for the x-axis ('time', 'linear', or 'band').
   * - ColumnChart: always 'band'
   * - LineChart: 'time' or 'linear' based on XAxis props
   */
  xScaleType?: XScaleType;

  /**
   * Format type for x-axis tick labels.
   * May differ from xScaleType in ColumnChart.
   * @default 'time'
   */
  xDataType?: XScaleType;

  /**
   * Optional callback invoked when zoom domain changes (drag-to-zoom).
   * Return false to prevent the default zoom behavior.
   * This allows consumers to implement custom zoom handling (e.g., updating URL params).
   */
  onZoomChange?: (domain: [number, number] | string[]) => boolean | void;

  /**
   * Explicit Y-axis tick values to ensure grid lines at specific positions.
   * When provided, these exact values are used for the Y-axis domain and ticks,
   * regardless of zoom state. When not provided, ticks are auto-computed from
   * the visible data range (enabling auto-scale during zoom).
   */
  yTickValues?: number[];

  /**
   * Y-axis configuration extracted from chart children via extractYAxisConfig.
   * Contains series data keys, reference points, formatting config, and domain constraints.
   */
  yAxisConfig?: YAxisConfig;

  /**
   * Callback invoked when the hovered data point changes.
   * Receives the full data point object when hovering over a data point,
   * or null when the cursor leaves the chart or moves away from data points.
   *
   * This is useful for syncing external UI with the chart hover state.
   */
  onDataPointHover?: (dataPoint: ChartDataPoint | null) => void;

  /**
   * Custom title for the empty state.
   * Shown when chart has no data to display.
   * @default "No data to show"
   */
  emptyStateTitle?: string;

  /**
   * Custom subtitle for the empty state.
   * Shown when chart has no data to display.
   * @default "Try adjusting the filter settings"
   */
  emptyStateSubtitle?: string;
};

function getFallbackDomain(dataType?: DataType): { min: number; max: number } {
  switch (dataType) {
    case 'percentage':
      return { min: 0, max: 1 };
    case 'money':
      return { min: 0, max: 100 };
    case 'time':
      return { min: 0, max: 1000 };
    case 'integer':
      return { min: 0, max: 10 };
    case 'number':
    default:
      return { min: -1, max: 1 };
  }
}

/** Returns true if any numeric value is Infinity, -Infinity, or effectively infinite (|v| >= 1e15). */
function dataContainsEffectivelyInfinite(data: ChartDataPoint[] | null | undefined): boolean {
  if (!data?.length) {
    return false;
  }
  for (const point of data) {
    for (const value of Object.values(point)) {
      if (typeof value === 'number' && isEffectivelyInfinite(value)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Hook that manages chart state (zoom, series metadata, reference lines, etc.).
 * When used inside SyncedChartProvider, zoom state is shared across charts.
 * When used outside SyncedChartProvider, each chart has independent zoom state.
 *
 * xDataKey, xScaleType, and xDataType are extracted from XAxis children by the chart
 * component and passed in directly - no more useEffect state updates from XAxis.
 */
export function useChartProviderState({
  chartType,
  data,
  referenceLineData,
  disableDragToZoom = false,
  isLoading = false,
  xDataKey: xDataKeyProp = INDEX_X_DATA_KEY,
  xScaleType: xScaleTypeProp,
  xDataType: xDataTypeProp = 'time',
  onZoomChange,
  yTickValues: yTickValuesProp,
  yAxisConfig,
  onDataPointHover,
  emptyStateTitle,
  emptyStateSubtitle,
}: ChartProviderStateProps): ChartContextValue {
  const {
    seriesDataKeys = [],
    yAxisNumTicks = 5,
    domainInclusionPoints = [],
    referenceLinePoints = [],
    yAxisFallbackMin,
    yAxisFallbackMax,
    yAxisDataType,
    useStackedYDomain = false,
    domainMin,
    domainMax,
  } = yAxisConfig ?? {};

  // Generate unique clip path ID for this chart instance
  const clipPathId = useId();
  const chartClipPathId = `chart-clip${clipPathId}`;

  // Determine xScaleType: use prop if provided, else default based on chart type
  const xScaleType = xScaleTypeProp ?? (chartType === 'column' ? 'band' : 'time');
  const xDataType = xDataTypeProp;
  const xDataKey = xDataKeyProp;

  // Transform data: add index keys. No clamping—Infinity is handled by AreaSeries; formatters display it.
  const transformedData = useMemo(() => {
    if (!data) {
      return null;
    }
    return data.map((point, index) => ({
      ...point,
      [INDEX_X_DATA_KEY]: index,
    }));
  }, [data]);

  // Track series display metadata (names, colors, dataKeys) for tooltips
  const [seriesDisplayMetadata, setSeriesDisplayMetadata] = useState(new Map<string, SeriesDisplayMetadata>());

  // Convert referenceLineData to Map for efficient lookup
  const referenceLines = useMemo(() => {
    if (!referenceLineData) {
      return new Map<string, ReferenceLineMetadata>();
    }
    return new Map(
      referenceLineData.map((refLine) => [
        refLine.key,
        {
          type: refLine.type,
          point: refLine.point,
          tooltipName: refLine.tooltipLabel,
          events: refLine.events,
        },
      ]),
    );
  }, [referenceLineData]);

  // Check if inside SyncedChartProvider for coordinated zoom
  const syncedZoom = useSyncedZoomContext();

  // Local zoom domain state (used when NOT inside SyncedChartProvider)
  const [localZoomDomain, setLocalZoomDomain] = useState<[number, number] | string[] | null>(null);

  // Use synced zoom state when inside SyncedChartProvider, otherwise use local state
  const zoomDomain = syncedZoom?.zoomDomain ?? localZoomDomain;
  const baseSetZoomDomain = syncedZoom?.setZoomDomain ?? setLocalZoomDomain;

  // Wrap setZoomDomain to call onZoomChange callback
  const setZoomDomain = useCallback(
    (domain: [number, number] | string[] | null) => {
      // If null (reset zoom), always allow it
      if (domain === null) {
        baseSetZoomDomain(domain);
        return;
      }

      // If callback provided, call it and check if it returns false (prevent default)
      if (onZoomChange) {
        const result = onZoomChange(domain);
        if (result === false) {
          // Consumer prevented default zoom behavior - always clear to ensure reset
          baseSetZoomDomain(null);
          return;
        }
      }

      // Apply zoom normally
      baseSetZoomDomain(domain);
    },
    [baseSetZoomDomain, onZoomChange],
  );

  // Compute visible data filtered by zoom domain (single source of truth for zoom filtering)
  const visibleData = useMemo(() => {
    if (!transformedData) {
      return [];
    }
    return filterDataByZoomDomain(transformedData, zoomDomain, xScaleType, xDataKey);
  }, [transformedData, zoomDomain, xScaleType, xDataKey]);

  // Compute Y-axis scale configuration from visible data (auto-scales during zoom)
  const { yScaleConfig, yTickValues } = useMemo(() => {
    // If legacy yTickValues prop is provided, use it (backward compatibility)
    if (yTickValuesProp && yTickValuesProp.length > 0) {
      const min = Math.min(...yTickValuesProp);
      const max = Math.max(...yTickValuesProp);
      return {
        yScaleConfig: {
          type: 'linear' as const,
          zero: chartType === 'column',
          nice: false,
          domain: [min, max] as [number, number],
        },
        yTickValues: yTickValuesProp,
      };
    }

    // No series data keys AND no horizontal reference lines - let visx auto-calculate the Y domain
    if (seriesDataKeys.length === 0 && domainInclusionPoints.length === 0) {
      return {
        yScaleConfig: {
          type: 'linear' as const,
          zero: chartType === 'column',
          nice: true,
        },
        yTickValues: undefined,
      };
    }

    // Use visible data for Y-domain calculation (enables auto-scale during zoom)
    const dataForDomain = visibleData.length > 0 ? visibleData : (transformedData ?? []);

    if (chartType === 'column') {
      let maxY = computeStackedYMax(dataForDomain, seriesDataKeys);
      for (const refValue of domainInclusionPoints) {
        maxY = Math.max(maxY, refValue);
      }
      if (domainMax !== undefined) {
        maxY = Math.max(maxY, domainMax);
      }

      const { niceMax, tickValues } = calculateNiceDomainWithTicks(0, maxY, yAxisNumTicks);

      return {
        yScaleConfig: {
          type: 'linear' as const,
          zero: true,
          nice: false,
          domain: [0, niceMax] as [number, number],
        },
        yTickValues: tickValues,
      };
    }

    // For line charts with AreaStackSeries, baseline is always 0
    if (chartType === 'line' && useStackedYDomain && seriesDataKeys.length > 0) {
      let maxY = computeStackedYMax(dataForDomain, seriesDataKeys);
      for (const refValue of domainInclusionPoints) {
        maxY = Math.max(maxY, refValue);
      }
      // Soft domainMax (e.g. exemplar fit mode) expands the stacked domain the same
      // way column charts do — without this, YAxis domainMax is ignored for AreaStackSeries.
      if (domainMax !== undefined) {
        maxY = Math.max(maxY, domainMax);
      }

      const { niceMax, tickValues } = calculateNiceDomainWithTicks(0, maxY, yAxisNumTicks);
      return {
        yScaleConfig: {
          type: 'linear' as const,
          zero: true,
          nice: false,
          domain: [0, niceMax] as [number, number],
        },
        yTickValues: tickValues,
      };
    }

    // For line charts (non-stacked), compute Y domain including reference line points
    let [yMin, yMax] = computeYDomain(dataForDomain, seriesDataKeys, domainInclusionPoints, {
      min: domainMin,
      max: domainMax,
    });

    // Remember the raw bounds (data + inclusion points + soft anchors) before
    // fallback/nicing so we can prevent padding from crossing zero later.
    const rawYMin = yMin;
    const rawYMax = yMax;

    // Apply fallback min/max when data has no range (e.g., all values identical, or Infinity excluded).
    // When data contains Infinity (e.g. AreaSeries bounds), use symmetric [-1, 1] so the area fills the chart.
    // Otherwise: explicit fallbackMin/fallbackMax from YAxis take precedence; else per-dataType defaults.
    if (yMin === yMax) {
      const hasInfinity = dataContainsEffectivelyInfinite(dataForDomain);
      const { min: defaultMin, max: defaultMax } = hasInfinity ? { min: -1, max: 1 } : getFallbackDomain(yAxisDataType);
      const effectiveMin = yAxisFallbackMin ?? defaultMin;
      const effectiveMax = yAxisFallbackMax ?? defaultMax;
      yMin = Math.min(yMin, effectiveMin);
      yMax = Math.max(yMax, effectiveMax);
    }

    // Don't let nicing/padding push the domain across zero when data doesn't cross zero.
    // hardMin/hardMax prevent misleading negative regions for non-negative data (and vice versa).
    const constraints = {
      hardMin: rawYMin >= 0 ? 0 : undefined,
      hardMax: rawYMax <= 0 ? 0 : undefined,
    };

    const { niceMin, niceMax, tickValues } = calculateNiceDomainWithTicks(
      yMin,
      yMax,
      yAxisNumTicks,
      referenceLinePoints,
      constraints,
    );

    return {
      yScaleConfig: {
        type: 'linear' as const,
        zero: false,
        nice: false, // Use our exact nice boundaries, don't let d3 re-round
        domain: [niceMin, niceMax] as [number, number],
      },
      yTickValues: tickValues,
    };
  }, [
    yTickValuesProp,
    yAxisConfig,
    visibleData,
    transformedData,
    chartType,
    seriesDataKeys,
    domainInclusionPoints,
    referenceLinePoints,
    useStackedYDomain,
    domainMin,
    domainMax,
    yAxisNumTicks,
    yAxisFallbackMin,
    yAxisFallbackMax,
    yAxisDataType,
  ]);

  // Create ordinal color scale for auto-assigning colors to series
  const colorScale = useMemo(() => createColorScale(), []);

  // Register/unregister series display metadata
  const registerSeriesDisplayMetadata = useCallback((key: string, metadata: SeriesDisplayMetadata) => {
    setSeriesDisplayMetadata((prev) => new Map(prev).set(key, metadata));
  }, []);

  const unregisterSeriesDisplayMetadata = useCallback((key: string) => {
    setSeriesDisplayMetadata((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // Reset zoom helper - use synced reset when inside SyncedChartProvider
  const localResetZoom = useCallback(() => {
    setLocalZoomDomain(null);
  }, []);

  const resetZoom = syncedZoom?.resetZoom ?? localResetZoom;

  return useMemo<ChartContextValue>(
    () => ({
      chartType,
      chartData: transformedData,
      visibleData,
      referenceLineData,
      xDataKey,
      xScaleType,
      xDataType,
      colorScale,
      seriesDisplayMetadata,
      registerSeriesDisplayMetadata,
      unregisterSeriesDisplayMetadata,
      referenceLines,
      zoomDomain,
      setZoomDomain,
      resetZoom,
      disableDragToZoom,
      isLoading,
      yAxisDataType,
      yTickValues,
      yScaleConfig,
      onDataPointHover,
      emptyStateTitle,
      emptyStateSubtitle,
      chartClipPathId,
    }),
    [
      chartType,
      transformedData,
      visibleData,
      referenceLineData,
      xDataKey,
      xScaleType,
      xDataType,
      colorScale,
      seriesDisplayMetadata,
      registerSeriesDisplayMetadata,
      unregisterSeriesDisplayMetadata,
      referenceLines,
      zoomDomain,
      setZoomDomain,
      resetZoom,
      disableDragToZoom,
      isLoading,
      yAxisDataType,
      yTickValues,
      yScaleConfig,
      onDataPointHover,
      emptyStateTitle,
      emptyStateSubtitle,
      chartClipPathId,
    ],
  );
}

/**
 * Provider component that manages chart state (zoom, series metadata, reference lines, etc.).
 * Does NOT render the chart - use ChartCanvas for rendering.
 *
 * @example
 * ```tsx
 * <ChartProvider value={value}>
 *   <ChartCanvas {...chartProps}>
 *     <XAxis dataKey="timestamp" />
 *     <Series data={data} />
 *   </ChartCanvas>
 * </ChartProvider>
 * ```
 */
export function ChartProvider({ value, children }: ChartProviderProps) {
  return (
    <ChartContext.Provider value={value}>
      <TooltipProvider>{children}</TooltipProvider>
    </ChartContext.Provider>
  );
}
